import type {
  CurrencyDisplayContext,
  FxBasis,
  FxDisplayReference,
  GroupedNode,
  LeafRequest,
  PositionMode,
  PriceDisplayMode,
  TradeCurrency,
} from "./contracts";
import { fmtEur2 } from "../../../lib/eex-format";

export interface PositionMonthFacts {
  bookedEUR: number;
  estimateEUR: number;
  reclassifiedEUR: number;
}

export type PositionPriceField = "avgLong" | "avgShort" | "netPrice" | "settle";

export interface PositionPriceFacts {
  value: number | null;
  currency: TradeCurrency;
  transactionCurrency: TradeCurrency;
  native: boolean;
  nativeValue: number | null;
  eurValue: number | null;
  eurAtSpot: number | null;
  eurAtForward: number | null;
  nativeAtSpot: number | null;
  nativeAtForward: number | null;
  spotRate: number | null;
  forwardRate: number | null;
  spotDate: string | null;
  forwardDate: string | null;
  spotSource: string | null;
  forwardSource: string | null;
  fxBasis: FxBasis;
  forwardAggregation: "single_month" | "delivery_weighted" | null;
  forwardMonths: string[];
  forwardMissingMonths: string[];
  forwardKinds: string[];
}

type DeliveryReference = FxDisplayReference & { kind?: string };
interface DeliveryPriceBucket {
  month: string;
  hours: number | null;
  markEUR: number | null;
  reference: DeliveryReference | null;
}

const finite = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);
const validRate = (reference: DeliveryReference | null | undefined): reference is DeliveryReference =>
  reference != null && finite(reference.rate) && reference.rate > 0;
const unique = (values: string[]): string[] => [...new Set(values)].sort();

/** A server-approved subtotal still needs the delivery profile of its open leaves. */
function priceDeliveryNode(node: GroupedNode, field: PositionPriceField): GroupedNode | null {
  if (field !== "netPrice" || !node.children.length || node.legs?.length) return node;
  const leaves: GroupedNode[] = [];
  const visit = (child: GroupedNode): void => {
    // A node with legs is a value leaf; its children are display slices of
    // that same matched residual, not independent positions to roll up again.
    if (child.legs != null) {
      if (child.netPrice != null || Math.round(child.netMW * 1e6) !== 0) leaves.push(child);
    } else child.children.forEach(visit);
  };
  node.children.forEach(visit);
  const first = leaves[0];
  if (!first) return null;
  const identity = (leaf: GroupedNode): string | null => {
    const leg = leaf.legs?.[0];
    if (!leg || leaf.netPrice == null || leaf.netPriceCurrency === null) return null;
    const currency = leaf.netPriceCurrency ?? leg.currency ?? leaf.valuationCurrency ?? "EUR";
    return JSON.stringify([
      leg.code, leg.expYear, leg.expMonth, currency, Math.sign(leaf.netMW),
      // The server permits same-contract/currency/side residuals across
      // provenance buckets. Only RON adds an FX delivery-profile constraint.
      currency === "RON" ? leaf.components?.map((part) => [part.deliveryMonth, part.deliveryHours]) : null,
    ]);
  };
  const firstIdentity = identity(first);
  return firstIdentity != null && leaves.every((leaf) => identity(leaf) === firstIdentity)
    ? first : null;
}

function deliveryPriceBuckets(
  node: GroupedNode | null,
  display: CurrencyDisplayContext
): DeliveryPriceBucket[] {
  if (!node) return [];
  const effectiveRates = display.eurRonDeliveryRates;
  const reference = (month: string, metadata?: {
    fxRate?: number | null; fxDate?: string | null; fxSource?: string | null; fxKind?: string | null;
  }): DeliveryReference | null => {
    // The effective map is authoritative, including explicit missing months.
    // It remains unchanged when the request switches to spot valuation.
    if (effectiveRates !== undefined) return effectiveRates[month] ?? null;
    if (metadata?.fxKind && metadata.fxKind !== "spot" && finite(metadata.fxRate) && metadata.fxRate > 0) {
      return { rate: metadata.fxRate, date: metadata.fxDate ?? "", source: metadata.fxSource ?? "", kind: metadata.fxKind };
    }
    const forward = display.eurRonForwards[month];
    return forward ? { ...forward, kind: "forward" } : null;
  };
  if (node.components?.length) {
    return node.components.map((part) => ({
      month: part.deliveryMonth,
      hours: part.deliveryHours,
      markEUR: part.referenceMarkEUR,
      reference: reference(part.deliveryMonth, part),
    }));
  }
  const leg = node.legs?.[0];
  // Older monthly payloads have no component schedule. Never extend this
  // single-month fallback to a quarter/year/season or unknown delivery shape.
  if (!leg || leg.code.slice(3) !== "M") return [];
  const month = `${leg.expYear}-${String(leg.expMonth).padStart(2, "0")}`;
  return [{ month, hours: 1, markEUR: node.referenceMarkEUR ?? node.mtmPrice, reference: reference(month, node) }];
}

/**
 * Price conversion follows the requested valuation basis. RON transaction
 * prices use sum(hours / rate), so the equivalent full-term rate is harmonic.
 * Native settlement prices convert each component's EUR mark before weighting.
 * The server remains the owner of position valuation and P&L.
 */
export function selectPositionPrice(
  node: GroupedNode,
  field: PositionPriceField,
  mode: PriceDisplayMode,
  display: CurrencyDisplayContext,
  fxBasis: FxBasis = "forward"
): PositionPriceFacts {
  const deliveryNode = priceDeliveryNode(node, field);
  const leg = node.legs?.[0] ?? deliveryNode?.legs?.[0];
  const currency = (field === "netPrice" ? node.netPriceCurrency : undefined) ??
    leg?.currency ?? node.valuationCurrency ?? "EUR";
  const spot = validRate(display.eurRonSpot) ? display.eurRonSpot : null;
  const spotRate = spot?.rate ?? null;
  const buckets = currency === "RON" ? deliveryPriceBuckets(deliveryNode, display) : [];
  const forwardMonths = unique(buckets.map((bucket) => bucket.month));
  const forwardMissingMonths = unique(buckets.filter((bucket) =>
    !validRate(bucket.reference) || !finite(bucket.hours) || bucket.hours <= 0
  ).map((bucket) => bucket.month));
  const forwardComplete = buckets.length > 0 && forwardMissingMonths.length === 0;
  const totalHours = forwardComplete ? buckets.reduce((sum, bucket) => sum + bucket.hours!, 0) : 0;
  const transactionForwardRate = forwardComplete
    ? totalHours / buckets.reduce((sum, bucket) => sum + bucket.hours! / bucket.reference!.rate, 0)
    : null;
  const rawValue = field === "netPrice" && (node.netPriceCurrency === null || !deliveryNode)
    ? null // Unknown or mixed open economic bases cannot acquire a guessed FX rate.
    : field === "settle" ? node.referenceMarkEUR ?? node.mtmPrice : node[field];
  const raw = finite(rawValue) ? rawValue : null;
  const settlement = field === "settle";
  const nativeAtSpot = settlement && raw != null
    ? currency === "RON" ? spotRate != null ? raw * spotRate : null : currency === "EUR" ? raw : null
    : null;
  const nativeAtForward = settlement && raw != null
    ? currency === "RON"
      ? forwardComplete && buckets.every((bucket) => finite(bucket.markEUR))
        ? buckets.reduce((sum, bucket) => sum + bucket.hours! * bucket.markEUR! * bucket.reference!.rate, 0) / totalHours
        : null
      : currency === "EUR" ? raw : null
    : null;
  // A zero EUR mark can have a nonzero converted monthly mean. There is then
  // no meaningful equivalent scalar rate, although its native price is valid.
  const forwardRate = settlement
    ? currency === "RON" && raw != null && raw !== 0 && nativeAtForward != null ? nativeAtForward / raw : null
    : transactionForwardRate;
  const eurAtSpot = currency === "EUR" ? raw
    : currency === "RON" ? settlement ? raw : raw != null && spotRate != null ? raw / spotRate : null
    : null;
  const eurAtForward = currency === "EUR" ? raw
    : currency === "RON" ? settlement ? raw : raw != null && transactionForwardRate != null ? raw / transactionForwardRate : null
    : null;
  const nativeValue = settlement && currency !== "GBP"
    ? fxBasis === "spot" ? nativeAtSpot : nativeAtForward
    : raw;
  const eurValue = fxBasis === "spot" ? eurAtSpot : eurAtForward;
  const references = buckets.flatMap((bucket) => validRate(bucket.reference) ? [bucket.reference] : []);
  const dates = unique(references.map((ref) => ref.date).filter(Boolean));
  const sources = unique(references.map((ref) => ref.source).filter(Boolean));

  return {
    value: mode === "native" ? nativeValue : eurValue,
    currency: mode === "native" ? currency : "EUR",
    transactionCurrency: currency,
    native: mode === "native" && currency !== "EUR",
    nativeValue,
    eurValue,
    eurAtSpot,
    eurAtForward,
    nativeAtSpot,
    nativeAtForward,
    spotRate,
    forwardRate,
    spotDate: spot?.date ?? null,
    forwardDate: dates.length === 1 ? dates[0]! : dates.length > 1 ? "Multiple dates" : null,
    spotSource: spot?.source ?? null,
    forwardSource: sources.length === 1 ? sources[0]! : sources.length > 1 ? "Multiple references" : null,
    fxBasis,
    forwardAggregation: buckets.length > 1 ? "delivery_weighted" : buckets.length === 1 ? "single_month" : null,
    forwardMonths,
    forwardMissingMonths,
    forwardKinds: unique(references.map((ref) => ref.kind ?? "forward")),
  };
}

/**
 * Builds the exact authorized leaf request from the clicked server-owned node.
 * No economic values are copied into a second presentation model.
 */
export function selectLeafRequestFromNode(
  node: GroupedNode,
  mode: PositionMode
): LeafRequest | null {
  const leg = node.legs?.[0];
  if (!leg) return null;
  return {
    code: leg.code,
    expYear: leg.expYear,
    expMonth: leg.expMonth,
    mode,
    tradeOrigin: leg.tradeOrigin ?? "cleared",
    currency: leg.currency ?? null,
    settlementIndex: leg.settlementIndex ?? null,
    bucket: leg.bucket,
  };
}

/** Sum only the selected server-provided month buckets; never blend fact kinds. */
export function selectPositionMonthFacts(
  node: GroupedNode,
  keys: readonly string[]
): PositionMonthFacts | null {
  let bookedEUR = 0;
  let estimateEUR = 0;
  let reclassifiedEUR = 0;
  let hasValue = false;
  for (const key of keys) {
    const value = node.months?.[key];
    if (value) {
      hasValue = true;
      bookedEUR += value.b;
      estimateEUR += value.e;
      reclassifiedEUR += value.r ?? 0;
    }
  }
  return hasValue ? { bookedEUR, estimateEUR, reclassifiedEUR } : null;
}

export interface PositionVmFacts {
  // Cleared + bilateral (each only when present); null renders "—".
  value: number | null;
  // cleared: cash only · bilateral: shadow ΔMtM only (italic) · mixed: both
  // (a subtotal with a bilateral part, marked) · none: nothing to show.
  kind: "cleared" | "bilateral" | "mixed" | "none";
  partial: boolean;
  title: string;
}

const fmtVmEur = (value: number): string => `€${fmtEur2(value)}`;

/** Windowed VM cell: the displayed amount plus the cleared/bilateral split for the tooltip. */
export function selectPositionVmFacts(node: GroupedNode): PositionVmFacts {
  const cell = node.vmWindow;
  if (!cell || (cell.cleared == null && cell.bilateral == null)) {
    return {
      value: null,
      kind: "none",
      partial: cell?.incomplete ?? false,
      title: cell?.incomplete ? "Incomplete VM: a required settlement price or contract size is missing"
        : cell?.unsupported ? "No supported EUR settlement reference"
        : cell ? "Bilateral deals are excluded from the VM column" : "",
    };
  }
  const kind = cell.cleared != null && cell.bilateral != null
    ? "mixed"
    : cell.bilateral != null ? "bilateral" : "cleared";
  const lines = [
    cell.cleared != null ? `Cleared VM (exchange settlement): ${fmtVmEur(cell.cleared)}` : null,
    cell.bilateral != null
      ? `Bilateral shadow VM (change in MtM vs exchange settlement, not cash): ${fmtVmEur(cell.bilateral)}`
      : null,
    cell.inherited !== 0
      ? `Includes ${fmtVmEur(cell.inherited)} inherited from ${
          cell.inheritedFrom.length > 0 ? cell.inheritedFrom.join(", ") : "cascaded parents"
        } (cascade look-through)`
      : null,
    cell.incomplete ? "Partial: a settlement day in the window had no settlement price" : null,
    cell.unsupported ? "Excludes deals without an EUR reference (GBP-priced, or RON not indexed to OPCOM DAM)" : null,
  ];
  return {
    value: Math.round(((cell.cleared ?? 0) + (cell.bilateral ?? 0)) * 100) / 100,
    kind,
    partial: cell.incomplete,
    title: lines.filter(Boolean).join("\n"),
  };
}
